import { useQuery } from '@apollo/client';
import { isEqual, uniqWith } from 'lodash-es';
import moment from 'moment/moment';
import React from 'react';
import * as styles from './TopicActivitiesPanel.module.scss';
import ActivityList from '../activities/ActivityList';
import RightPanel from '../app/layout/RightPanel';
import EmptyState from '../components/EmptyState';
import { gql } from '../graphql/generated/gql';
import { AlphaTopicActivityAction } from '../graphql/generated/graphql';
import CreatePostButton from '../post/CreatePostButton';
import useSlug from '../utils/router/useSlug';

const TOPIC_ACTIVITIES_END_OF_TIME = '2099-12-31T00:00:00.0Z';

const TOPIC_ACTIVITIES_QUERY = gql(/** @complexity 14 */ `
  query topicActivities($id: ID!, $beforeAt: Datetime!, $actions: [AlphaTopicActivityAction!]) {
    alphaTopicActivities(beforeAt: $beforeAt, topicIds: [$id], actions: $actions) {
      ...ActivityList_activities
    }
  }
`);

const ALL_ACTION_TYPES = Object.values(AlphaTopicActivityAction);

const TopicActivitiesPanel = () => {
  const [hasMoreActivities, setHasMoreActivities] = React.useState(true);
  const [topicId] = useSlug('topicSlug');
  const response = useQuery(TOPIC_ACTIVITIES_QUERY, {
    variables: {
      id: topicId,
      beforeAt: TOPIC_ACTIVITIES_END_OF_TIME,
      actions: ALL_ACTION_TYPES,
    },
  });

  const activities = response.data?.alphaTopicActivities;

  const fetchMoreActivities = async () => {
    const lastActivity = response.data?.alphaTopicActivities?.at(-1);
    if (!lastActivity) {
      return;
    }

    // There might be two activities with the same timestamp on the edge of two pages.
    // To ensure we don't skip any of them, we slightly over-fetch to include
    // the last activity from the previous page on the next page.
    const beforeAt = moment(lastActivity.actedAt).add(1, 'ms').toISOString();

    await response.fetchMore({
      variables: {
        id: topicId,
        beforeAt,
        actions: ALL_ACTION_TYPES,
      },
      updateQuery(previousResult, { fetchMoreResult }) {
        const hasNewActivities = fetchMoreResult.alphaTopicActivities.some(
          (item) => item.actedAt !== lastActivity.actedAt,
        );

        if (!hasNewActivities) {
          setHasMoreActivities(false);
        }

        return {
          ...previousResult,
          alphaTopicActivities: uniqWith(
            [
              ...previousResult.alphaTopicActivities,
              ...fetchMoreResult.alphaTopicActivities,
            ],
            isEqual,
          ),
        };
      },
    });
  };

  return (
    <RightPanel.Inner header="Activity">
      <div className={styles.content}>
        {activities?.length ? (
          <ActivityList
            activities={activities}
            fetchMore={hasMoreActivities ? fetchMoreActivities : null}
          />
        ) : (
          <EmptyState
            title="No activity yet…"
            message="Activity will appear after a post has been created under this topic."
            action={
              <CreatePostButton
                size="large"
                variant="secondary"
                topicId={topicId}
              >
                Create a post
              </CreatePostButton>
            }
            vertical
          />
        )}
      </div>
    </RightPanel.Inner>
  );
};

export default TopicActivitiesPanel;
